import React, { useMemo } from 'react';
import {
  Card, Box, Typography, Stack, ToggleButtonGroup, ToggleButton, useTheme, useMediaQuery, alpha, Chip,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

const SERIES_COLORS = ['#FE5F01', '#3B82F6', '#10B981', '#F59E0B'];

const formatTick = (d) => {
  if (!d) return '';
  const [, m, day] = String(d).split('-');
  return `${day}/${m}`;
};

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 12px 24px rgba(20,26,56,0.20)',
        border: '1px solid',
        borderColor: 'divider',
        p: 1.25,
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 700, mb: 0.5 }}>
        {formatTick(label)}
      </Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{p.name}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{fmtNum(p.value)}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

const resolveHeight = (height, isMobile, isTablet) => {
  if (typeof height === 'object' && height !== null) {
    return height;
  }
  const base = typeof height === 'number' ? height : 320;
  if (isMobile) return Math.min(base, 260);
  if (isTablet) return Math.min(base, 300);
  return base;
};

const AreaTrendCard = ({ title, subtitle, seriesMap = {}, growth = {}, activeKey, onSelect, height = 320 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const chartHeight = resolveHeight(height, isMobile, isTablet);
  const keys = useMemo(() => Object.keys(seriesMap), [seriesMap]);
  const current = activeKey || keys[0];

  const data = useMemo(() => {
    // Merge every series by its date key (not array index), so series of
    // differing length / ordering line up correctly on the same day.
    const byDate = new Map();
    keys.forEach((k) => {
      (seriesMap[k] || []).forEach((row) => {
        if (!row || row.date == null) return;
        let merged = byDate.get(row.date);
        if (!merged) { merged = { date: row.date }; byDate.set(row.date, merged); }
        merged[k] = row.count ?? 0;
      });
    });
    const rows = Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    rows.forEach((r) => keys.forEach((k) => { if (r[k] == null) r[k] = 0; }));
    return rows;
  }, [seriesMap, keys]);

  const g = growth[current];
  const positive = (g ?? 0) >= 0;
  const activeColor = SERIES_COLORS[keys.indexOf(current) % SERIES_COLORS.length] || SERIES_COLORS[0];

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, height: '100%', width: '100%', maxWidth: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        mb={2}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {typeof g === 'number' && (
            <Chip
              size="small"
              icon={positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              label={`${positive ? '+' : ''}${g}%`}
              sx={{
                bgcolor: alpha(positive ? theme.palette.success.main : theme.palette.error.main, 0.12),
                color: positive ? theme.palette.success.dark : theme.palette.error.dark,
                fontWeight: 800,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}
          {keys.length > 1 && (
            <ToggleButtonGroup
              value={current}
              exclusive
              size="small"
              onChange={(_, v) => v && onSelect && onSelect(v)}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': { textTransform: 'capitalize', px: { xs: 1, sm: 1.5 }, py: 0.5 },
              }}
            >
              {keys.map((k) => (
                <ToggleButton key={k} value={k}>{k}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>

      <Box sx={{ width: '100%', height: chartHeight, minHeight: 200 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id={`area-${current}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={activeColor} stopOpacity={0.4} />
                <stop offset="95%" stopColor={activeColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 6"
              vertical={false}
              stroke={alpha(theme.palette.grey[400], 0.4)}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTick}
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              minTickGap={24}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              width={36}
              allowDecimals={false}
            />
            <Tooltip content={<TrendTooltip />} cursor={{ stroke: alpha(activeColor, 0.3), strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey={current}
              stroke={activeColor}
              fill={`url(#area-${current})`}
              strokeWidth={2.5}
              name={(current || '').charAt(0).toUpperCase() + (current || '').slice(1)}
              activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff', fill: activeColor }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default React.memo(AreaTrendCard);
